import { useState } from 'react';
import { useMutation } from 'react-apollo';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';
import produce from 'immer';

import {
  cardElementConfigurationCreate as cardElementConfigurationCreateMutation,
  cardElementConfigurationUpdate as cardElementConfigurationUpdateMutation,
} from '../../../apps/cards/graphql/mutations/cardElementConfiguration';
import cardDetails from '../../../services/cards/graphql/fragments/cardDetails';
import { getCardDesignerCardElementConfigurationInputByElement } from './helpers';

/**
 * CardElementConfiguration hook.
 *
 * @param {Object} options The options.
 * @param {Object} [options.card] The Card.
 * @returns {{onElementConfigurationChange: (function(*=, *=): Promise<void>), cardElementLoading: unknown}}
 */
const useCardDesignerCardElementConfiguration = ({ card }) => {
  const [cardElementLoading, setCardElementLoading] = useState(null);
  const [cardElementConfigurationCreate] = useMutation(
    cardElementConfigurationCreateMutation,
  );
  const [cardElementConfigurationUpdate] = useMutation(
    cardElementConfigurationUpdateMutation,
  );

  const onElementConfigurationChange = async ({
    element,
    config = {},
    elementConfig = {},
  }) => {
    const { cardElement, cardElementConfiguration, cardSide } = element;
    const existingCardElementConfiguration =
      cardSide.card_element_configurations?.find(
        x => x.card_element.id === cardElement.id,
      );

    setCardElementLoading(cardElement);

    if (existingCardElementConfiguration) {
      // Update the CardElementConfiguration if it exists.
      await cardElementConfigurationUpdate({
        variables: {
          input: {
            card_element_configuration_id: cardElementConfiguration.id,
            ...config,
            ...getCardDesignerCardElementConfigurationInputByElement(
              element,
              elementConfig,
              config.is_editable,
            ),
          },
        },
      });
    } else {
      // Create the CardElementConfiguration for this CardSide if it does not exist.
      await cardElementConfigurationCreate({
        variables: {
          input: {
            card_element_id: cardElement.id,
            card_side_id: cardSide.id,
            ...config,
            ...getCardDesignerCardElementConfigurationInputByElement(
              element,
              elementConfig,
              config.is_editable,
            ),
          },
        },
        update: (cache, { data }) => {
          const cachedCard = cache.readFragment({
            id: defaultDataIdFromObject(card),
            fragment: cardDetails,
            fragmentName: 'cardDetails',
          });

          const newCardElementConfiguration =
            data.cardElementConfigurationCreate.card_element_configuration;

          const updatedCard = produce(cachedCard, draft => {
            draft.card_side_groups.records[0].card_sides.records
              .find(x => x.id === cardSide.id)
              .card_element_configurations.push(newCardElementConfiguration);
          });

          cache.writeFragment({
            id: defaultDataIdFromObject(card),
            fragment: cardDetails,
            fragmentName: 'cardDetails',
            data: updatedCard,
          });
        },
      });
    }

    setCardElementLoading(null);
  };

  return {
    onElementConfigurationChange,
    cardElementLoading,
  };
};

export default useCardDesignerCardElementConfiguration;
